import type { ISignalProviderListCard, SignalProviderVerifiedStatus } from '@brushfire/types'

export type SignalProviderUserSortColumn =
  | 'firstName'
  | 'lastName'
  | 'state'
  | 'verifiedStatus'

type SignalProviderSortColumn = SignalProviderUserSortColumn | 'joinedAt'

export type SignalProviderSortDirection = 'asc' | 'desc'

export interface SignalProviderSortState {
  column: SignalProviderUserSortColumn | null
  direction: SignalProviderSortDirection | null
}

export const DEFAULT_SIGNAL_PROVIDER_SORT: SignalProviderSortState = {
  column: null,
  direction: null,
}

const VERIFIED_ORDER: Record<SignalProviderVerifiedStatus, number> = {
  verified: 0,
  unverified: 1,
  failed: 2,
}

function compareStrings(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' })
}

function resolveEffectiveSort(sort: SignalProviderSortState): {
  column: SignalProviderSortColumn
  direction: SignalProviderSortDirection
} {
  if (sort.column && sort.direction) {
    return { column: sort.column, direction: sort.direction }
  }
  return { column: 'joinedAt', direction: 'desc' }
}

export function sortDirectionForColumn(
  sort: SignalProviderSortState,
  column: SignalProviderUserSortColumn,
): SignalProviderSortDirection | null {
  return sort.column === column ? sort.direction : null
}

export function sortSignalProviderCards(
  items: ISignalProviderListCard[],
  sort: SignalProviderSortState,
): ISignalProviderListCard[] {
  const { column, direction } = resolveEffectiveSort(sort)
  const sorted = [...items]
  const dir = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    let result = 0
    switch (column) {
      case 'firstName':
        result = compareStrings(a.firstName, b.firstName)
        break
      case 'lastName':
        result = compareStrings(a.lastName, b.lastName)
        break
      case 'state':
        result = compareStrings(a.state, b.state)
        break
      case 'verifiedStatus':
        result = VERIFIED_ORDER[a.verifiedStatus] - VERIFIED_ORDER[b.verifiedStatus]
        break
      case 'joinedAt':
        result = new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime()
        break
      default:
        result = 0
    }
    if (result === 0 && column !== 'joinedAt') {
      result = new Date(b.joinedAt).getTime() - new Date(a.joinedAt).getTime()
    }
    return result * dir
  })

  return sorted
}

/** Same 3-state cycle as Sensemaker dashboard tables: asc → desc → clear (default joinedAt desc). */
export function toggleSortColumn(
  current: SignalProviderSortState,
  column: SignalProviderUserSortColumn,
): SignalProviderSortState {
  if (current.column !== column) {
    return { column, direction: 'asc' }
  }
  if (current.direction === 'asc') {
    return { column, direction: 'desc' }
  }
  return { column: null, direction: null }
}
